import { Fetcher } from '@this/src/util';
import React, { useEffect, useState } from 'react';

import { styled } from '@this/constants/themes';
import Link from '@this/components/shared/atoms/link';
import type User from '@this/domain/user/user';
import { ByMedia } from '@this/shared/ui/other/by_media';
import HeaderContent from '@this/shared/header/header_content';
import { headerHeight } from '@this/shared/header/header.style';
import HeaderContentSm from '@this/shared/header/header_content_sm';
import type { CurrentMembershipResponseArgs } from '@this/shared/header/organization_switcher';
import { getColor } from '@this/shared/ui/theme';
import { AppBanner } from '@this/components/shared/app_banner';
import { getEnvironmentDisplayName, getEnvironmentKind } from '@this/components/shared/header/environment_kind';

interface Props {
  user?: User | null;
  ignoreMenu?: boolean;
}

interface EnvironmentInfo {
  branch_name?: string;
  release_version?: string;
}

interface EnvironmentDisplayProps {
  environmentName: string;
  environmentInfo: EnvironmentInfo;
}

const EnvironmentDisplay: React.FC<EnvironmentDisplayProps> = ({ environmentName, environmentInfo }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginLeft: '15px',
        color: '#fff',
        fontSize: '11px',
        opacity: 0.9
      }}
    >
      <div>{environmentName}</div>
      {environmentInfo.branch_name && (
        <div style={{ marginTop: '2px' }}>ブランチ名: {environmentInfo.branch_name}</div>
      )}
      {environmentInfo.release_version && (
        <div style={{ marginTop: '2px' }}>リリース番号: {environmentInfo.release_version}</div>
      )}
    </div>
  );
};

const Header = ({ user, ignoreMenu }: Props) => {
  const [appliedCount, setAppliedCount] = useState<number>(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>(0);
  const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo>({});
  const [currentMembership, setCurrentMembership] = useState<CurrentMembershipResponseArgs | null>(null);

  useEffect(() => {
    if (user === null) return;
    const getAppliedCount = async () => {
      const res = await Fetcher.get<{ applied_count: number }>('/application/in_advance/applied_count');
      setAppliedCount(res.applied_count);
    };
    getAppliedCount();
  }, []);

  useEffect(() => {
    if (!user || !user.id) return;
    const getCurrentMembership = async () => {
      try {
        const res = await Fetcher.get<CurrentMembershipResponseArgs>('/current_membership.json');
        setCurrentMembership(res);
      } catch {
        setCurrentMembership(null);
      }
    };
    getCurrentMembership();
  }, []);

  useEffect(() => {
    if (user === null) {
      return () => {};
    }

    const shouldFetchUnreadCount = () => {
      const path = location.pathname;
      const targetPaths = ['/organization', '/dashboard', '/trips', '/application/in_advance'];
      const shouldFetch = targetPaths.includes(path);
      return shouldFetch;
    };

    const fetchUnreadCount = async () => {
      try {
        const res = await Fetcher.get<{ unread_count: number }>('/unread_messages/count.json');
        setUnreadMessagesCount(res.unread_count);
      } catch (e) {
        console.error('Failed to fetch unread count:', e);
        setUnreadMessagesCount(0);
      }
    };

    if (shouldFetchUnreadCount()) {
      fetchUnreadCount();
    }

    const handleUnreadMessagesUpdated = () => {
      fetchUnreadCount();
    };
    window.addEventListener('unreadMessagesUpdated', handleUnreadMessagesUpdated);
    return () => {
      window.removeEventListener('unreadMessagesUpdated', handleUnreadMessagesUpdated);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    const getEnvironmentInfo = async () => {
      try {
        const res = (await Fetcher.get('/environment_info')) as EnvironmentInfo;
        setEnvironmentInfo(res);
      } catch (e) {
        // 本番環境など、環境情報が取得できない場合は無視
        setEnvironmentInfo({});
      }
    };
    getEnvironmentInfo();
  }, []);

  const dashbordLocation = () => {
    if (ignoreMenu !== undefined && ignoreMenu === true) {
      return '#';
    }
    if (user && user.id) {
      return user.reservationAccepted ? '/dashboard' : '/trips';
    }
    return '/';
  };

  const environmentKind = getEnvironmentKind(new URL(location.href).hostname);

  return (
    <>
      <AppBanner />
      <TheHeader className="noprint">
        <HeaderIn>
          <h1>
            <Logo to={dashbordLocation} />
          </h1>
          {environmentKind && (
            <EnvironmentDisplay
              environmentName={getEnvironmentDisplayName(environmentKind)}
              environmentInfo={environmentInfo}
            />
          )}
          {(ignoreMenu === undefined || ignoreMenu === false) && (
            <ByMedia>
              {matches =>
                matches.sm ? (
                  <HeaderContentSm
                    user={user}
                    currentMembership={currentMembership}
                    appliedCount={appliedCount}
                    unreadMessagesCount={unreadMessagesCount}
                  />
                ) : (
                  <HeaderContent
                    user={user}
                    currentMembership={currentMembership}
                    appliedCount={appliedCount}
                    unreadMessagesCount={unreadMessagesCount}
                  />
                )
              }
            </ByMedia>
          )}
        </HeaderIn>
      </TheHeader>
    </>
  );
};

const TheHeader = styled.header`
  width: 100%;
  height: ${headerHeight};
`;

const HeaderIn = styled.div`
  display: flex;
  align-items: center;
  background: ${getColor('background', 'tertiary')};
  border-bottom: 1px solid ${getColor('brand', 'primary')};
  height: ${headerHeight};
`;

const Logo = styled(Link)`
  display: block;
  width: 173px;
  height: 28px;
  margin-left: 10px;
  background-image: url('${props => props.theme.logoPath}');
  background-repeat: no-repeat;
  background-size: contain;
`;

export default Header;
